import { useCallback, useMemo, useReducer, useRef } from "react";
import { showToastError } from "../../../utils/helpers/toastUtils";
import type { SaveBeforeLeaveResult } from "../core/saveBeforeLeaveOutcome";
import {
    initialUnsavedChangesGuardModalState,
    runSaveAndContinue,
    SAVE_AND_CONTINUE_VALIDATION_FAILED_TOAST,
    shouldPromptBeforeProceed,
    unsavedChangesGuardModalReducer,
} from "../core/guardState";
import type { UnsavedChangesModalProps } from "./UnsavedChangesModal";

export type UseUnsavedChangesGuardOptions = {
    isDirty: boolean;
    /** When provided, enables 3-button mode (Save and continue). */
    onSave?: () => Promise<boolean | SaveBeforeLeaveResult>;
    /** Override dismiss label. Defaults to "Cancel" when onSave is set, otherwise "Stay". */
    cancelLabel?: string;
    title?: string;
    message?: string;
    continueWithoutSavingLabel?: string;
    /** Optional external saving flag (e.g. shared with a page-level Save button). */
    saving?: boolean;
    /** Called before the modal is dismissed (e.g. release a blocked route navigation). */
    onBeforeDismiss?: () => void;
};

export type UseUnsavedChangesGuardResult = {
    requestProceed: (proceed: () => void) => void;
    modalProps: UnsavedChangesModalProps;
    dismiss: () => void;
    isOpen: boolean;
    saving: boolean;
};

export function useUnsavedChangesGuard({
    isDirty,
    onSave,
    cancelLabel,
    title,
    message,
    continueWithoutSavingLabel,
    saving: externalSaving = false,
    onBeforeDismiss,
}: UseUnsavedChangesGuardOptions): UseUnsavedChangesGuardResult {
    const [modalState, dispatch] = useReducer(
        unsavedChangesGuardModalReducer,
        initialUnsavedChangesGuardModalState
    );
    const pendingProceedRef = useRef<(() => void) | null>(null);
    const onSaveRef = useRef(onSave);
    const onBeforeDismissRef = useRef(onBeforeDismiss);
    onSaveRef.current = onSave;
    onBeforeDismissRef.current = onBeforeDismiss;

    const dismiss = useCallback(() => {
        onBeforeDismissRef.current?.();
        pendingProceedRef.current = null;
        dispatch({ type: "dismiss" });
    }, []);

    const runPendingProceed = useCallback(() => {
        const proceed = pendingProceedRef.current;
        pendingProceedRef.current = null;
        dispatch({ type: "dismiss" });
        proceed?.();
    }, []);

    const requestProceed = useCallback(
        (proceed: () => void) => {
            if (!shouldPromptBeforeProceed(isDirty)) {
                proceed();
                return;
            }
            pendingProceedRef.current = proceed;
            dispatch({ type: "open" });
        },
        [isDirty]
    );

    const handleContinueWithoutSaving = useCallback(() => {
        runPendingProceed();
    }, [runPendingProceed]);

    const handleSaveAndContinue = useCallback(async () => {
        const save = onSaveRef.current;
        if (!save) return;

        dispatch({ type: "saveStart" });
        try {
            const result = await runSaveAndContinue(save, runPendingProceed);
            if (result === "validation_failed") {
                dismiss();
                showToastError(SAVE_AND_CONTINUE_VALIDATION_FAILED_TOAST);
                return;
            }
            if (result === "stayed") {
                dispatch({ type: "saveEnd" });
            }
        } catch {
            dispatch({ type: "saveEnd" });
        }
    }, [runPendingProceed, dismiss]);

    const resolvedCancelLabel = cancelLabel ?? (onSave ? "Cancel" : "Stay");
    const saving = externalSaving || modalState.saving;

    const modalProps = useMemo(
        (): UnsavedChangesModalProps => ({
            show: modalState.isOpen,
            saving,
            title,
            message,
            cancelLabel: resolvedCancelLabel,
            continueWithoutSavingLabel,
            onCancel: dismiss,
            onContinueWithoutSaving: handleContinueWithoutSaving,
            ...(onSave ? { onSaveAndContinue: () => void handleSaveAndContinue() } : {}),
        }),
        [
            modalState.isOpen,
            saving,
            title,
            message,
            continueWithoutSavingLabel,
            resolvedCancelLabel,
            dismiss,
            handleContinueWithoutSaving,
            onSave,
            handleSaveAndContinue,
        ]
    );

    return {
        requestProceed,
        modalProps,
        dismiss,
        isOpen: modalState.isOpen,
        saving,
    };
}
